import { useList, useGo, useDelete } from "@refinedev/core";
import { useState } from "react";
import {
  Plus,
  Eye,
  FileText,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  File,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type KnowledgeDocument, FILE_TYPE_LABELS } from './types';
import { notify } from '@/lib/notify';

/**
 * Форматирование размера файла
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Получить иконку по типу файла
 */
function getFileIcon(mimeType: string) {
  if (mimeType === "application/pdf") {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv") {
    return <FileText className="h-4 w-4 text-green-500" />;
  }
  if (mimeType.includes("wordprocessing")) {
    return <FileText className="h-4 w-4 text-blue-500" />;
  }
  return <File className="h-4 w-4 text-muted-foreground" />;
}

/**
 * Knowledge Base Documents list page
 */
export function KnowledgeDocumentsList() {
  useScrollRestoration();
  const go = useGo();
  const [page, setPage] = usePersistedState("knowledge-documents:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("knowledge-documents:sort", { field: "createdAt", order: "desc" });
  const [deleteItems, setDeleteItems] = useState<KnowledgeDocument[]>([]);
  const [search, setSearch] = usePersistedState("knowledge-documents:search", "");
  const [statusFilter, setStatusFilter] = usePersistedState<string>("knowledge-documents:statusFilter", "ALL");
  const [reprocessingIds, setReprocessingIds] = useState<Set<number>>(new Set());
  const pageSize = 10;

  const listResult = useList<KnowledgeDocument>({
    resource: "knowledge-documents",
    pagination: { currentPage: page, pageSize, mode: "server" },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters: [
      ...(search ? [{ field: "search", operator: "contains" as const, value: search }] : []),
      ...(statusFilter !== "ALL" ? [{ field: "status", operator: "eq" as const, value: statusFilter }] : []),
    ],
  });

  const data = listResult.result?.data ?? [];
  const total = listResult.result?.total ?? 0;
  const isLoading = listResult.query?.isLoading ?? false;
  const refetch = listResult.query?.refetch;

  const { mutate: deleteDoc } = useDelete();

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    const count = deleteItems.length;
    for (const item of deleteItems) {
      deleteDoc({
        resource: "knowledge-documents",
        id: item.id,
        successNotification: false,
        errorNotification: false,
      });
    }
    notify.success(`Удалено: ${count}`);
    setDeleteItems([]);
  };

  /**
   * Повторная обработка документов
   */
  const handleReprocess = async (items: KnowledgeDocument[]) => {
    const eligibleItems = items.filter(
      (item) => item.status === "FAILED" || item.status === "COMPLETED"
    );

    if (eligibleItems.length === 0) {
      notify.error("Выберите документы со статусом 'Ошибка' или 'Готов'");
      return;
    }

    const newReprocessingIds = new Set(reprocessingIds);
    for (const item of eligibleItems) {
      newReprocessingIds.add(item.id);
    }
    setReprocessingIds(newReprocessingIds);

    let successCount = 0;
    let failCount = 0;

    for (const item of eligibleItems) {
      try {
        const response = await fetch(`/api/knowledge-base/${item.id}/reprocess`, {
          method: "POST",
          credentials: "include",
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    setReprocessingIds(new Set());
    refetch?.();

    if (successCount > 0) {
      notify.success(`${successCount} документ(ов) добавлено в очередь на обработку`);
    }
    if (failCount > 0) {
      notify.error(`Не удалось обработать ${failCount} документ(ов)`);
    }
  };

  const getStatusBadge = (status: KnowledgeDocument["status"]) => {
    switch (status) {
      case "PENDING":
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
            <Clock className="h-3 w-3 mr-1" />
            В очереди
          </Badge>
        );
      case "PROCESSING":
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-950/20">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Обработка
          </Badge>
        );
      case "COMPLETED":
        return (
          <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 dark:bg-green-950/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Готов
          </Badge>
        );
      case "FAILED":
        return (
          <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 dark:bg-red-950/20">
            <XCircle className="h-3 w-3 mr-1" />
            Ошибка
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const columns: Column<KnowledgeDocument>[] = [
    {
      key: "id",
      header: "ID",
      width: "60px",
      sortable: true,
    },
    {
      key: "filename",
      header: "Файл",
      sortable: true,
      render: (_, doc) => (
        <div className="flex items-center gap-2">
          {getFileIcon(doc.mimeType)}
          <div className="min-w-0">
            <p className="font-medium truncate max-w-[300px]" title={doc.filename}>
              {doc.filename}
            </p>
            <p className="text-xs text-muted-foreground">
              {FILE_TYPE_LABELS[doc.mimeType] || doc.mimeType} • {formatFileSize(doc.fileSize)}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Статус",
      sortable: true,
      width: "140px",
      render: (value) => getStatusBadge(value as KnowledgeDocument["status"]),
    },
    {
      key: "chunksCount",
      header: "Чанки",
      sortable: true,
      width: "100px",
      render: (value, doc) => (
        <span className={doc.status === "COMPLETED" ? "text-green-600 font-medium" : "text-muted-foreground"}>
          {value || 0}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Загружен",
      sortable: true,
      width: "120px",
      render: (value) => formatDate(String(value)),
    },
  ];

  const bulkActions: BulkAction<KnowledgeDocument>[] = [
    {
      label: "Просмотр",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/knowledge-documents/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Повторить обработку",
      icon: <RotateCcw className="h-4 w-4" />,
      onClick: handleReprocess,
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="База знаний"
        description="Документы для AI ассистента (RAG)"
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch?.()} title="Обновить">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={() => go({ to: "/knowledge-documents/create" })}>
              <Plus className="h-4 w-4 mr-2" />
              Загрузить документ
            </Button>
          </div>
        )}
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder="Поиск по названию файла..."
        filtersCount={statusFilter !== "ALL" ? 1 : 0}
        onResetFilters={() => { setStatusFilter("ALL"); setPage(1); }}
        filters={
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Статус</span>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Все статусы</SelectItem>
                <SelectItem value="PENDING">В очереди</SelectItem>
                <SelectItem value="PROCESSING">Обработка</SelectItem>
                <SelectItem value="COMPLETED">Готов</SelectItem>
                <SelectItem value="FAILED">Ошибка</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(doc) => go({ to: `/knowledge-documents/show/${doc.id}` })}
        emptyMessage="Нет документов. Загрузите файлы для базы знаний."
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? "Удалить документ?"
                : `Удалить ${deleteItems.length} документов?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить документ «{deleteItems[0].filename}»?
                  Все связанные векторные данные также будут удалены. Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} документов?
                  Все связанные векторные данные также будут удалены. Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
